import { createMassagerSchema, type CreateMassagerFormData } from '@/schemas/createMassagerSchema';
import type { ClinicLocation } from '@/types/admin-clinic-detail';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type SubmitHandler, useWatch } from 'react-hook-form';
import useAppToast from '@/hooks/useAppToast';
import { normalizeLocationId } from '@/utils/locationId';
import { SERVICE_TYPE_A2, SERVICE_TYPE_BOTH, SERVICE_TYPE_R2 } from '@/constants/serviceType';

type SortDir = 'ascend' | 'descend';

function isWideAreaRowsEmpty(
  rows:
    | Array<{
        wide_area_union_code?: string | null;
        wide_area_union_number?: string | null;
        prefecture_number?: string | null;
      }>
    | null
    | undefined
): boolean {
  const first = rows?.[0];
  return Boolean(
    rows &&
    rows.length === 1 &&
    !String(first?.wide_area_union_code ?? '').trim() &&
    !String(first?.wide_area_union_number ?? '').trim() &&
    !String(first?.prefecture_number ?? '').trim()
  );
}

function resolveInitialClinicId(
  fixedClinicId: number | null | undefined,
  initialClinicId: number | null | undefined,
  clinicOptions: { id: number }[]
): number {
  if (typeof fixedClinicId === 'number' && fixedClinicId > 0) return fixedClinicId;
  if (typeof initialClinicId === 'number' && initialClinicId > 0) return initialClinicId;
  return clinicOptions[0]?.id ?? 0;
}

export type InsurerSortField =
  | 'wide_area_union_code'
  | 'wide_area_union_number'
  | 'prefecture_number';

export type ClinicServiceType =
  | typeof SERVICE_TYPE_A2
  | typeof SERVICE_TYPE_R2
  | typeof SERVICE_TYPE_BOTH;

interface UseCreateMassagerFormLogicProps {
  clinicOptions: { id: number; clinic_number: string }[];
  clinicLocationOptions: ClinicLocation[];
  initialClinicId?: number | null;
  fixedClinicId?: number | null;
  /** A2: はり/きゅう + acu_moxi dates. R2: 免許番号 + massage dates. */
  clinicServiceType?: ClinicServiceType | null;
  /** UpSystem サイトかどうか（登録日を必須にする条件に使用） */
  isUpSystemSite?: boolean;
  /** Prefill for edit mode (from API show). When set, used as defaultValues. */
  initialValues?: Partial<CreateMassagerFormData>;
  /** Last visit date (Y-m-d) for the massager. Used for available_to validation in edit mode. */
  lastVisitDate?: string | null;
  onClinicIdChange?: (clinicId: number) => void;
  onSubmit?: (data: CreateMassagerFormData) => Promise<void>;
}

/** 新規作成用: 保険者別番号は単一行（flat）. Includes both A2 and R2 fields. */
const baseDefaultValues: CreateMassagerFormData = {
  clinic_id: 0,
  clinic_locations_a2: [
    {
      clinic_location_id: 0,
      available_from: null,
      available_to: null,
      regist_date: null,
      stop_request_date: null,
    },
  ],
  clinic_locations_r2: [
    {
      clinic_location_id: 0,
      available_from: null,
      available_to: null,
      regist_date: null,
      stop_request_date: null,
    },
  ],
  name: '',
  name_kana: null,
  login_id: '',
  password: '',
  email: null,
  acupuncture_license_number: null,
  moxibustion_license_number: null,
  license_number: null,
  wide_area_union_code: null,
  wide_area_union_number: null,
  prefecture_number: null,
  wide_area_union_rows: [
    { wide_area_union_code: null, wide_area_union_number: null, prefecture_number: null },
  ],
  massage_wide_area_union_rows: [
    { wide_area_union_code: null, wide_area_union_number: null, prefecture_number: null },
  ],
};

export const useCreateMassagerFormLogic = ({
  clinicOptions,
  clinicLocationOptions,
  initialClinicId,
  fixedClinicId,
  clinicServiceType = SERVICE_TYPE_A2,
  isUpSystemSite = false,
  initialValues,
  lastVisitDate = null,
  onClinicIdChange,
  onSubmit,
}: UseCreateMassagerFormLogicProps) => {
  // Use refs so the resolver always reads the latest values without re-initializing useForm.
  const clinicServiceTypeRef = useRef(clinicServiceType);
  clinicServiceTypeRef.current = clinicServiceType;
  const isUpSystemSiteRef = useRef(isUpSystemSite);
  isUpSystemSiteRef.current = isUpSystemSite;
  const clinicLocationOptionsRef = useRef(clinicLocationOptions);
  clinicLocationOptionsRef.current = clinicLocationOptions;
  const lastVisitDateRef = useRef(lastVisitDate);
  lastVisitDateRef.current = lastVisitDate;

  const resolver = useCallback(
    (values: CreateMassagerFormData, ctx: unknown, opts: unknown) => {
      const st = clinicServiceTypeRef.current;
      const schema = createMassagerSchema(
        st === SERVICE_TYPE_BOTH ? SERVICE_TYPE_BOTH : st === SERVICE_TYPE_R2 ? SERVICE_TYPE_R2 : SERVICE_TYPE_A2,
        isUpSystemSiteRef.current,
        clinicLocationOptionsRef.current,
        lastVisitDateRef.current
      );
      return zodResolver(schema)(values, ctx as never, opts as never);
    },
    [] // stable — reads latest via refs on every call
  );

  const toast = useAppToast();
  const methods = useTrimmedForm<CreateMassagerFormData>({
    resolver,
    // Validate onChange so dropdown errors (拠点選択など) clear immediately
    // when user selects a valid option, without requiring blur.
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: initialValues
      ? { ...baseDefaultValues, ...initialValues }
      : {
          ...baseDefaultValues,
          clinic_id: resolveInitialClinicId(fixedClinicId, initialClinicId, clinicOptions),
          clinic_locations_a2: [
            {
              clinic_location_id: 0,
              available_from: null,
              available_to: null,
              regist_date: null,
              stop_request_date: null,
            },
          ],
          clinic_locations_r2: [
            {
              clinic_location_id: 0,
              available_from: null,
              available_to: null,
              regist_date: null,
              stop_request_date: null,
            },
          ],
        },
  });

  const { control, setValue, handleSubmit, reset, clearErrors, getValues, watch } = methods;

  // Clear field error when user types (only show error when submit, clear when typing)
  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name) clearErrors(name as Parameters<typeof clearErrors>[0]);
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);
  const appliedInitialValuesRef = useRef<string | null>(null);

  // Reset form from API values only when source data really changes and form is not being edited.
  // This keeps user's typing when submit fails (e.g. unique validation error).
  useEffect(() => {
    if (!initialValues) return;

    const snapshot = JSON.stringify(initialValues);
    const firstApply = appliedInitialValuesRef.current === null;
    const sourceChanged = appliedInitialValuesRef.current !== snapshot;

    // Always sync form with latest initialValues from API when they change,
    // even if the form is currently dirty. This ensures that after a successful
    // save + refetch (edit mode), the UI reflects cleared/updated backend state.
    if (firstApply || sourceChanged) {
      reset({ ...baseDefaultValues, ...initialValues });
      appliedInitialValuesRef.current = snapshot;
    }
  }, [initialValues, reset]);

  const watchedClinicId = useWatch({ control, name: 'clinic_id', defaultValue: 0 });
  const watchedClinicLocationsA2 = useWatch({
    control,
    name: 'clinic_locations_a2',
    defaultValue: [{ clinic_location_id: 0 }],
  });
  const watchedClinicLocationsR2 = useWatch({
    control,
    name: 'clinic_locations_r2',
    defaultValue: [{ clinic_location_id: 0 }],
  });
  const watchedPrefectureNumber = useWatch({
    control,
    name: 'prefecture_number',
    defaultValue: '',
  });
  const watchedWideAreaRows = useWatch({ control, name: 'wide_area_union_rows', defaultValue: [] });

  // Prefecture Filter: 新規は単一行、編集は配列で絞り込み
  const [prefectureFilter, setPrefectureFilter] = useState('');
  const [massagePrefectureFilter, setMassagePrefectureFilter] = useState('');
  const isEditMode = Boolean(initialValues);
  const rows = Array.isArray(watchedWideAreaRows) ? watchedWideAreaRows : [];
  const filteredRows = !prefectureFilter.trim()
    ? rows
    : rows.filter(r =>
        (r?.prefecture_number ?? '').toString().trim().includes(prefectureFilter.trim())
      );
  const rowMatchesFilter = isEditMode
    ? filteredRows.length > 0
    : !prefectureFilter.trim() ||
      (watchedPrefectureNumber ?? '').toString().trim().includes(prefectureFilter.trim());

  // Sort State: RA2 DataTables default = 保険者コード ascending
  const [insurerSortField, setInsurerSortField] = useState<InsurerSortField | null>(
    'wide_area_union_code'
  );
  const [insurerSortDir, setInsurerSortDir] = useState<SortDir>('ascend');
  /** 表示順（編集時のみ）。入力中は変えず、ソート列クリック時のみ更新（RA2: 保存後に並び替えではなくクリック時にのみソート） */
  const [displayOrder, setDisplayOrder] = useState<number[]>([]);
  const previousClinicIdRef = useRef<number | null>(null);
  const clinicIdDirty = Boolean(methods.formState.dirtyFields?.clinic_id);
  const watchedAcuLicense = useWatch({
    control,
    name: 'acupuncture_license_number',
    defaultValue: null,
  });
  const watchedMoLicense = useWatch({
    control,
    name: 'moxibustion_license_number',
    defaultValue: null,
  });
  const watchedMassageLicense = useWatch({
    control,
    name: 'license_number',
    defaultValue: null,
  });
  const prevA2HasLicenseRef = useRef<boolean | null>(null);
  const prevR2HasLicenseRef = useRef<boolean | null>(null);

  // 編集時: 行数が変わったら表示順を [0,1,...,n-1] に初期化
  useEffect(() => {
    if (isEditMode && rows.length > 0) {
      setDisplayOrder(prev => {
        if (prev.length !== rows.length) return rows.map((_, i) => i);
        return prev;
      });
    } else {
      setDisplayOrder([]);
    }
  }, [isEditMode, rows.length]);

  // 編集時: ソート列クリック時 or 初回読み込み時のみ表示順を再計算（入力中は並び替えない）
  useEffect(() => {
    if (!isEditMode || rows.length === 0) return;
    const sortField = insurerSortField ?? 'wide_area_union_code';
    const indices = Array.from({ length: rows.length }, (_, i) => i);
    const sorted = [...indices].sort((a, b) => {
      const va = (rows[a]?.[sortField] ?? '').toString().trim();
      const vb = (rows[b]?.[sortField] ?? '').toString().trim();
      const cmp = va.localeCompare(vb, 'ja');
      return insurerSortDir === 'ascend' ? cmp : -cmp;
    });
    setDisplayOrder(sorted);
  }, [insurerSortField, insurerSortDir, isEditMode, rows.length]);

  // Effects
  // Notify parent when clinic_id changes (used to refetch clinic locations).
  useEffect(() => {
    if (onClinicIdChange && watchedClinicId) onClinicIdChange(watchedClinicId);
  }, [watchedClinicId, onClinicIdChange]);

  // When clinic_id changes, keep basic info but clear clinic-dependent A2/R2 fields.
  useEffect(() => {
    if (previousClinicIdRef.current === null) {
      previousClinicIdRef.current = watchedClinicId;
      return;
    }
    if (previousClinicIdRef.current === watchedClinicId) return;
    previousClinicIdRef.current = watchedClinicId;
    // On first hydration in edit mode, clinic_id can transition from 0 -> actual value.
    // Do not clear dependent fields unless clinic_id was changed by user interaction.
    if (!clinicIdDirty) return;

    // Use reset with current snapshot to force-sync field-array UI + form values.
    const currentValues = getValues();
    reset(
      {
        ...currentValues,
        clinic_locations_a2: [
          {
            clinic_location_id: 0,
            available_from: null,
            available_to: null,
            regist_date: null,
            stop_request_date: null,
          },
        ],
        clinic_locations_r2: [
          {
            clinic_location_id: 0,
            available_from: null,
            available_to: null,
            regist_date: null,
            stop_request_date: null,
          },
        ],
        acupuncture_license_number: null,
        moxibustion_license_number: null,
        license_number: null,
        wide_area_union_code: null,
        wide_area_union_number: null,
        prefecture_number: null,
        wide_area_union_rows: [
          { wide_area_union_code: null, wide_area_union_number: null, prefecture_number: null },
        ],
        massage_wide_area_union_rows: [
          { wide_area_union_code: null, wide_area_union_number: null, prefecture_number: null },
        ],
      },
      {
        keepSubmitCount: true,
      }
    );
    clearErrors(['wide_area_union_rows', 'massage_wide_area_union_rows']);
    setPrefectureFilter('');
    setMassagePrefectureFilter('');
  }, [watchedClinicId, clinicIdDirty, reset, clearErrors, getValues]);

  // When creating a new massager: after clinicOptions are loaded, if the current clinic_id
  // does not exist in the list, automatically correct it to the first clinic option.
  // Only do this when clinic_id is 0. Do not overwrite a clinic_id that came from the URL or user selection.
  useEffect(() => {
    if (initialValues) return; // 編集時は API からの clinic_id をそのまま使う
    if (clinicOptions.length === 0) return;
    const exists = clinicOptions.some(c => c.id === watchedClinicId);
    // watchedClinicId が 0（未設定）の場合のみ、自動で先頭に補正。
    // 0 以外（例: URL からの 475）の場合は、リストに存在しなくても変更しない。
    // ただしユーザーが一度選択/クリアした後（dirty の場合）は勝手に補正しない。
    if (!exists && !watchedClinicId && !clinicIdDirty) {
      const firstId = clinicOptions[0]?.id ?? 0;
      if (firstId > 0) {
        setValue('clinic_id', firstId, { shouldDirty: true });
      }
    }
  }, [clinicOptions, watchedClinicId, initialValues, setValue]);

  // Instant-clear dependent fields when A2 licenses are removed (create/edit).
  useEffect(() => {
    const ac = (watchedAcuLicense ?? '').toString().trim();
    const mo = (watchedMoLicense ?? '').toString().trim();
    const hasA2 = ac !== '' || mo !== '';
    if (prevA2HasLicenseRef.current === null) {
      prevA2HasLicenseRef.current = hasA2;
      return;
    }

    // Edit screen requirement: do NOT auto-clear other A2 fields when license is cleared.
    if (isEditMode) {
      prevA2HasLicenseRef.current = hasA2;
      return;
    }

    // Only clear when user changes from "has license" -> "no license".
    if (prevA2HasLicenseRef.current && !hasA2) {
      const currentA2Rows = getValues('wide_area_union_rows') ?? [];
      const isA2RowsAlreadyEmpty = isWideAreaRowsEmpty(currentA2Rows);

      const currentValues = getValues();
      reset(
        {
          ...currentValues,
          wide_area_union_code: null,
          wide_area_union_number: null,
          prefecture_number: null,
          clinic_locations_a2: [
            {
              clinic_location_id: 0,
              available_from: null,
              available_to: null,
              regist_date: null,
              stop_request_date: null,
            },
          ],
          wide_area_union_rows: isA2RowsAlreadyEmpty
            ? currentA2Rows
            : [
                {
                  wide_area_union_code: null,
                  wide_area_union_number: null,
                  prefecture_number: null,
                },
              ],
        },
        {
          keepSubmitCount: true,
        }
      );
      clearErrors([
        'wide_area_union_code',
        'wide_area_union_number',
        'prefecture_number',
        'wide_area_union_rows',
        'clinic_locations_a2',
      ]);
      setPrefectureFilter('');
    }
    prevA2HasLicenseRef.current = hasA2;
  }, [
    watchedAcuLicense,
    watchedMoLicense,
    clearErrors,
    setPrefectureFilter,
    getValues,
    reset,
    isEditMode,
  ]);

  // Instant-clear dependent fields when R2 license is removed (create/edit).
  useEffect(() => {
    const massage = (watchedMassageLicense ?? '').toString().trim();
    const hasR2 = massage !== '';
    if (prevR2HasLicenseRef.current === null) {
      prevR2HasLicenseRef.current = hasR2;
      return;
    }

    // Edit screen requirement: do NOT auto-clear other R2 fields when license is cleared.
    if (isEditMode) {
      prevR2HasLicenseRef.current = hasR2;
      return;
    }

    // Only clear when user changes from "has license" -> "no license".
    if (prevR2HasLicenseRef.current && !hasR2) {
      const currentR2Rows = getValues('massage_wide_area_union_rows') ?? [];
      const isR2RowsAlreadyEmpty = isWideAreaRowsEmpty(currentR2Rows);

      const currentValues = getValues();
      reset(
        {
          ...currentValues,
          clinic_locations_r2: [
            {
              clinic_location_id: 0,
              available_from: null,
              available_to: null,
              regist_date: null,
              stop_request_date: null,
            },
          ],
          massage_wide_area_union_rows: isR2RowsAlreadyEmpty
            ? currentR2Rows
            : [
                {
                  wide_area_union_code: null,
                  wide_area_union_number: null,
                  prefecture_number: null,
                },
              ],
        },
        {
          keepSubmitCount: true,
        }
      );
      clearErrors(['massage_wide_area_union_rows', 'clinic_locations_r2']);
    }
    setMassagePrefectureFilter('');
    prevR2HasLicenseRef.current = hasR2;
  }, [
    watchedMassageLicense,
    clearErrors,
    getValues,
    reset,
    setMassagePrefectureFilter,
    isEditMode,
  ]);

  // Only clear 拠点選択 when options have loaded and current value is not in the list.
  useEffect(() => {
    if (clinicLocationOptions.length === 0) return;
    const validLocationIds = new Set(
      clinicLocationOptions.map(loc => normalizeLocationId(loc.id)).filter(id => id > 0)
    );
    const sanitizeLocationRows = (
      rows: Array<
        | {
            clinic_location_id: number;
            available_from?: string | null;
            available_to?: string | null;
            regist_date?: string | null;
            stop_request_date?: string | null;
          }
        | { clinic_location_id?: number | null }
      >
    ) =>
      rows.map(item => {
        const id = normalizeLocationId(item?.clinic_location_id);
        const af = (item as { available_from?: string | null })?.available_from ?? null;
        const at = (item as { available_to?: string | null })?.available_to ?? null;
        const rd = (item as { regist_date?: string | null })?.regist_date ?? null;
        const sd = (item as { stop_request_date?: string | null })?.stop_request_date ?? null;
        return {
          clinic_location_id: validLocationIds.has(id) ? id : 0,
          available_from: af,
          available_to: at,
          regist_date: rd,
          stop_request_date: sd,
        };
      });

    const locationsA2 = Array.isArray(watchedClinicLocationsA2) ? watchedClinicLocationsA2 : [];
    const updatedA2 = sanitizeLocationRows(locationsA2);
    if (
      updatedA2.some(
        (item, i) =>
          normalizeLocationId(locationsA2[i]?.clinic_location_id) !== item.clinic_location_id
      )
    ) {
      setValue('clinic_locations_a2', updatedA2);
    }
    const locationsR2 = Array.isArray(watchedClinicLocationsR2) ? watchedClinicLocationsR2 : [];
    const updatedR2 = sanitizeLocationRows(locationsR2);
    if (
      updatedR2.some(
        (item, i) =>
          normalizeLocationId(locationsR2[i]?.clinic_location_id) !== item.clinic_location_id
      )
    ) {
      setValue('clinic_locations_r2', updatedR2);
    }
  }, [clinicLocationOptions, watchedClinicLocationsA2, watchedClinicLocationsR2, setValue]);

  const handleFormSubmit: SubmitHandler<CreateMassagerFormData> = async data => {
    const clinic_location_ids_a2 = (data.clinic_locations_a2 ?? [])
      .map(c => c?.clinic_location_id ?? 0)
      .filter(id => id > 0);
    const clinic_location_ids_r2 = (data.clinic_locations_r2 ?? [])
      .map(c => c?.clinic_location_id ?? 0)
      .filter(id => id > 0);
    const payload = {
      ...data,
      clinic_location_ids_a2,
      clinic_location_ids_r2,
      name_kana: data.name_kana ?? null,
      // login_id and password are required string fields; keep as-is ('' when empty) so inline errors show correctly.
      login_id: data.login_id,
      password: data.password,
      email: data.email ?? null,
      acupuncture_license_number: data.acupuncture_license_number ?? null,
      moxibustion_license_number: data.moxibustion_license_number ?? null,
      license_number: data.license_number ?? null,
    };
    if (initialValues) {
      const wideAreaRows = (data.wide_area_union_rows ?? []).filter(
        r =>
          (r.wide_area_union_code ?? '').trim() !== '' ||
          (r.wide_area_union_number ?? '').trim() !== '' ||
          (r.prefecture_number ?? '').trim() !== ''
      );
      (payload as Record<string, unknown>).wide_area_union_rows = wideAreaRows.length
        ? wideAreaRows
        : undefined;
    } else {
      (payload as Record<string, unknown>).wide_area_union_code = data.wide_area_union_code ?? null;
      (payload as Record<string, unknown>).wide_area_union_number =
        data.wide_area_union_number ?? null;
      (payload as Record<string, unknown>).prefecture_number = data.prefecture_number ?? null;
    }
    try {
      await onSubmit?.(payload as CreateMassagerFormData);
    } catch (error: unknown) {
      const anyErr = error as any;
      const fieldErrors = anyErr?.response?.data?.errors as
        | Record<string, string[] | string | Record<string, string[] | string>>
        | undefined;

      const normalizeFieldName = (rawField: string): string => {
        if (!rawField) return rawField;
        // Laravel custom messages can sometimes come as rule-qualified keys (e.g. login_id.unique)
        if (rawField === 'login_id' || rawField.startsWith('login_id.')) return 'login_id';
        if (rawField === 'password' || rawField.startsWith('password.')) return 'password';
        if (rawField === 'email' || rawField.startsWith('email.')) return 'email';
        if (rawField === 'name' || rawField.startsWith('name.')) return 'name';
        if (rawField === 'name_kana' || rawField.startsWith('name_kana.')) return 'name_kana';
        if (rawField === 'massage_license_number' || rawField.startsWith('massage_license_number.'))
          return 'license_number';
        // Backend uses clinic_location_ids_* but form uses clinic_locations_*
        if (rawField.startsWith('clinic_location_ids_a2.'))
          return rawField.replace('clinic_location_ids_a2.', 'clinic_locations_a2.');
        if (rawField.startsWith('clinic_location_ids_r2.'))
          return rawField.replace('clinic_location_ids_r2.', 'clinic_locations_r2.');
        return rawField;
      };

      const pickMessage = (
        value: string[] | string | Record<string, string[] | string>
      ): string | null => {
        if (typeof value === 'string') return value;
        if (Array.isArray(value)) return typeof value[0] === 'string' ? value[0] : null;
        if (value && typeof value === 'object') {
          const firstNested = Object.values(value)[0];
          if (typeof firstNested === 'string') return firstNested;
          if (Array.isArray(firstNested) && typeof firstNested[0] === 'string')
            return firstNested[0];
        }
        return null;
      };

      const relatedDataMessages = [
        'すでにこの拠点の設定期間内で訪問データが登録されています。施術者編集を行う場合は、対象拠点の訪問データを削除してから再度実行してください。',
        'すでにこの施術者で訪問データが登録されています。施術者編集を行う場合は、対象拠点の訪問データを削除してから再度実行してください。',
      ];
      const managerMessage = '拠点の施術管理者に設定されています。解除してから操作してください。';
      let showedToast = false;
      let mapped = false;

      if (fieldErrors && typeof fieldErrors === 'object') {
        Object.entries(fieldErrors).forEach(([field, messages]) => {
          const message = pickMessage(messages);
          const normalizedField = normalizeFieldName(field);
          if (typeof message === 'string' && message.trim() !== '') {
            if (relatedDataMessages.includes(message) || message === managerMessage) {
              if (!showedToast) {
                toast.error(message);
                showedToast = true;
              }
              return;
            }

            methods.setError(normalizedField as any, {
              type: 'server',
              message,
            });
            mapped = true;
          }
        });
      }
      if (!showedToast && !mapped) {
        toast.error('エラーが発生しました。');
      }
      // Do not rethrow so that navigation logic in caller is not executed
    }
  };

  const handleSubmitWithFocus = handleSubmit(handleFormSubmit, errors => {
    const keys = Object.keys(errors);
    if (keys.length > 0) {
      methods.setFocus(keys[0] as any);
    }
  });

  return {
    methods,
    control,
    handleSubmit: handleSubmitWithFocus,
    prefectureFilter,
    setPrefectureFilter,
    massagePrefectureFilter,
    setMassagePrefectureFilter,
    rowMatchesFilter,
    insurerSortField,
    setInsurerSortField,
    insurerSortDir,
    setInsurerSortDir,
    displayOrder,
    isSubmitted: methods.formState.isSubmitted,
  };
};
